import React, { createContext, useContext, useState, useEffect } from 'react';
import { CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';
import configuredConfig from '../config/cognito-config';
import { useUsersApi } from '../apis/users-apis';
import { UserData } from '../types';

import { 
  User, 
  SystemInfo, 
  AuthContextType, 
  UserRole,
  PERMISSIONS,
  ROLE_PERMISSIONS,
  Permission
} from '../types/auth-types';

// Initialize the user pool first
const userPool = new CognitoUserPool({
  UserPoolId: configuredConfig.userPoolId,
  ClientId: configuredConfig.clientId,
});

// Create the context with a default value
const defaultContextValue: AuthContextType = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  isAdmin: false,
  systemInfo: null,
  signInWithGoogle: async () => {},
  handleOAuthCallback: async () => false,
  signOut: async () => false,
  getAuthToken: async () => null,
  setSystemInfo: () => {},
  hasPermission: () => false,
  checkAuthStatus: async () => false,
  refreshSession: async () => false,
};
const AuthContext = createContext<AuthContextType>(defaultContextValue);

// Keep track of the current user
let currentUser: CognitoUser | null = null;

export const setCurrentUser = (user: CognitoUser) => {
  currentUser = user;
};

const mockUser = {
  email: 'test@example.com',
  sub: 'test-sub-123',
  cognitoUsername: 'test-cognito-username-123',
  userId: 'test-sub-123',
  firstName: 'MockFirstName',
  lastName: 'MockLastName',
  role: 'ADMIN' as UserRole,
  // role: 'USER' as UserRole,
  // Add other required properties
};

// Create and export the provider component
export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Load user from localStorage if available
  const loadPersistedUser = () => {
    if (process.env.REACT_APP_SKIP_AUTH === 'true') {
      return mockUser;
    }
    
    try {
      const persistedUser = localStorage.getItem('userData');
      if (persistedUser) {
        return JSON.parse(persistedUser);
      }
    } catch (error) {
      console.error('Error loading persisted user data:', error);
    }
    return null;
  };

  // Load system info from localStorage if available
  const loadPersistedSystemInfo = () => {
    try {
      const persistedSystemInfo = localStorage.getItem('systemInfo');
      if (persistedSystemInfo) {
        return JSON.parse(persistedSystemInfo);
      }
    } catch (error) {
      console.error('Error loading persisted system info:', error);
    }
    return null;
  };

  const [user, setUser] = useState<User | null>(loadPersistedUser());
  const [isLoading, setIsLoading] = useState(true);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [systemInfo, setSystemInfoState] = useState<SystemInfo | null>(loadPersistedSystemInfo());
  const { getUser: getUserApi, createUser: createUserApi } = useUsersApi();

  const isAdmin = user?.role === 'ADMIN';

  // Enhanced setSystemInfo function that also persists to localStorage
  const setSystemInfo = (info: SystemInfo | null) => {
    setSystemInfoState(info);
    
    if (info) {
      localStorage.setItem('systemInfo', JSON.stringify(info));
    } else {
      localStorage.removeItem('systemInfo');
    }
  };

  // Enhanced user setter that also persists to localStorage
  const setUserWithPersistence = (newUser: User | null) => {
    setUser(newUser);
    
    if (newUser) {
      localStorage.setItem('userData', JSON.stringify(newUser));
    } else {
      localStorage.removeItem('userData');
    }
  };

  const hasPermission = (permission: string): boolean => {
    if (!user || !user.role) return false;
    
    // Verify that the role is a valid UserRole
    const role = user.role as UserRole;
    if (!Object.keys(ROLE_PERMISSIONS).includes(role)) return false;
    
    // Now TypeScript knows that role is a valid key of ROLE_PERMISSIONS
    return ROLE_PERMISSIONS[role].includes(permission as Permission);
  };

  const checkAuthStatus = async () => {
    try {
      console.log('Starting checkAuthStatus');

      // First check for stored tokens
      const accessToken = localStorage.getItem('accessToken');
      const idToken = localStorage.getItem('idToken');
      
      if (!accessToken || !idToken) {
        console.log('No stored tokens found');
        setIsLoading(false);
        return false;
      }

      // Verify token validity
      try {
        // Decode the ID token to get user information
        const idTokenPayload = JSON.parse(atob(idToken.split('.')[1]));
        
        // Check token expiration
        const currentTime = Math.floor(Date.now() / 1000);
        if (idTokenPayload.exp < currentTime) {
          console.log('Token expired');
          await signOut();
          return false;
        }

        // Token is valid, get user data from DynamoDB
        const userDDBData = await getUserApi(idTokenPayload.sub);
        const role: UserRole = userDDBData.isAdmin ? 'ADMIN' : 'USER';

        // Retrieve profile image from token or DynamoDB
        let profileImage = userDDBData.profileImage || null;
        if (!profileImage && (idTokenPayload.picture || idTokenPayload['google:picture'])) {
          profileImage = idTokenPayload.picture || idTokenPayload['google:picture'];
        }

        // Set user state
        const userData: User = {
          email: idTokenPayload.email,
          sub: idTokenPayload.sub,
          cognitoUsername: idTokenPayload['cognito:username'],
          userId: idTokenPayload.sub,
          firstName: idTokenPayload.given_name,
          lastName: idTokenPayload.family_name,
          role: role,
          profileImage: profileImage || undefined
        };

        setUserWithPersistence(userData);
        setAuthToken(accessToken);
        
        return true;
      } catch (error) {
        console.error('Error verifying token:', error);
        await signOut();
        return false;
      }

    } catch (error) {
      console.error('Error in checkAuthStatus:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshSession = async (): Promise<boolean> => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (!refreshToken) {
        console.log('No refresh token available');
        return false;
      }
      
      const tokenEndpoint = `${configuredConfig.cognitoDomain}/oauth2/token`;
      
      const response = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: configuredConfig.clientId,
          refresh_token: refreshToken
        }).toString()
      });
      
      if (!response.ok) {
        console.error('Failed to refresh tokens');
        return false;
      }
      
      const tokens = await response.json();
      
      // Store new tokens
      localStorage.setItem('accessToken', tokens.access_token);
      localStorage.setItem('idToken', tokens.id_token);
      setAuthToken(tokens.access_token);
      
      // Update user info from the new ID token
      const idTokenPayload = JSON.parse(atob(tokens.id_token.split('.')[1]));
      
      const userDDBData = await getUserApi(idTokenPayload.sub);
      const role: UserRole = userDDBData.isAdmin ? 'ADMIN' : 'USER';

      let profileImage = null;
      if (idTokenPayload.picture) {
        console.log('Profile image found in ID token payload:', idTokenPayload.picture);
        profileImage = idTokenPayload.picture;
      } 
      // Alternative: try the Google-specific format if available
      else if (idTokenPayload['picture'] || idTokenPayload['google:picture']) {
        console.log('Profile image found in ID token payload:', idTokenPayload['picture'] || idTokenPayload['google:picture']);
        profileImage = idTokenPayload['picture'] || idTokenPayload['google:picture'];
      }
      
      const userData: User = {
        email: idTokenPayload.email,
        sub: idTokenPayload.sub,
        cognitoUsername: idTokenPayload['cognito:username'],
        userId: idTokenPayload.sub,
        firstName: idTokenPayload.given_name,
        lastName: idTokenPayload.family_name,
        role: role,
        profileImage: profileImage || null
      };
      
      setUserWithPersistence(userData);
      
      return true;
    } catch (error) {
      console.error('Error refreshing session:', error);
      return false;
    }
  };

  const signInWithGoogle = async (): Promise<void> => {
    try {
      const redirectUri = `${configuredConfig.cloudfrontDomain}/oauth/callback`;
      const queryParams = new URLSearchParams({
        client_id: configuredConfig.clientId,
        response_type: 'code',
        scope: 'email openid profile',
        redirect_uri: redirectUri,
        identity_provider: 'Google'
      });
  
      const oauthUrl = `${configuredConfig.hostedUiDomain}/oauth2/authorize?${queryParams}`;
      console.log('Initiating Google sign in with URL:', oauthUrl);
      window.location.href = oauthUrl;
    } catch (error) {
      console.error('Error initiating Google sign in:', error);
      throw error;
    }
  };

  const handleOAuthCallback = async (code: string): Promise<boolean> => {
    try {
      console.log('handleOAuthCallback start');
      setIsLoading(true);
      
      const redirectUri = `${configuredConfig.cloudfrontDomain}/oauth/callback`;
      const tokenEndpoint = `${configuredConfig.cognitoDomain}/oauth2/token`;
      
      console.log('Token endpoint:', tokenEndpoint);
      
      const response = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: configuredConfig.clientId,
          code: code,
          redirect_uri: redirectUri,
        }).toString()
      });
  
      if (!response.ok) {
        const errorData = await response.text();
        console.error('Token endpoint error:', errorData);
        
        // Check for disabled user error in the response
        if (errorData.toLowerCase().includes('disabled')) {
          throw new Error('User is disabled');
        }
        
        throw new Error(`Failed to exchange code for tokens: ${errorData}`);
      }
  
      const tokens = await response.json();
      console.log('Tokens received with keys:', Object.keys(tokens));
      
      // Validate tokens before proceeding
      if (!tokens.access_token || !tokens.id_token) {
        throw new Error('Invalid token response');
      }
  
      localStorage.setItem('accessToken', tokens.access_token);
      localStorage.setItem('idToken', tokens.id_token);
      localStorage.setItem('refreshToken', tokens.refresh_token);
      setAuthToken(tokens.access_token);
  
      const idTokenPayload = JSON.parse(atob(tokens.id_token.split('.')[1]));
      console.log('ID token payload:', idTokenPayload);
  
      // Check if user is disabled in token payload
      if (idTokenPayload['cognito:user_status'] === 'DISABLED') {
        throw new Error('User is disabled');
      }

      let profileImage = null;
      if (idTokenPayload.picture) {
        console.log('Profile image found in ID token payload:', idTokenPayload.picture);
        profileImage = idTokenPayload.picture;
      } 
      // Alternative: try the Google-specific format if available
      else if (idTokenPayload['picture'] || idTokenPayload['google:picture']) {
        console.log('Profile image found in ID token payload:', idTokenPayload['picture'] || idTokenPayload['google:picture']);
        profileImage = idTokenPayload['picture'] || idTokenPayload['google:picture'];
      }

      await createUserApi({
        email: idTokenPayload.email,
        sub: idTokenPayload.sub,
        cognitoUsername: idTokenPayload['cognito:username'],
        isAdmin: false,
        isActive: true,
        lastLogin: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        ownedItems: {
          packageIds: [],
          skillIds: [],
          agentIds: [],
          adapterIds: []
        },
        allowedItems: {
          packageIds: [],
          skillIds: [],
          agentIds: [],
          adapterIds: []
        },
        profileImage: profileImage || null,
      });

      const userDDBData = await getUserApi(idTokenPayload.sub);
      console.log('User DDB data:', userDDBData);
      const role: UserRole = userDDBData.isAdmin ? 'ADMIN' : 'USER';

      const newUser: User = {
        email: idTokenPayload.email,
        sub: idTokenPayload.sub,
        cognitoUsername: idTokenPayload['cognito:username'],
        userId: idTokenPayload.sub,
        firstName: idTokenPayload.given_name,
        lastName: idTokenPayload.family_name,
        role: role,
        profileImage: profileImage || null
      };
      console.log('Setting user info:', newUser);
      setUserWithPersistence(newUser);
      
      const cognitoUser = userPool.getCurrentUser();
      if (cognitoUser) {
        cognitoUser.signOut();
      }
  
      const userData = {
        Username: idTokenPayload.email,
        Pool: userPool
      };
      const newCognitoUser = new CognitoUser(userData);
      setCurrentUser(newCognitoUser);
  
      await new Promise(resolve => setTimeout(resolve, 500));
  
      return true;
    } catch (error: any) {
      console.error('Error in handleOAuthCallback:', error);
      
      // Clean up any stored tokens on error
      localStorage.removeItem('accessToken');
      localStorage.removeItem('idToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('systemInfo');
      setAuthToken(null);
      setUserWithPersistence(null);
      
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async (): Promise<boolean> => {
    console.log('AuthContext: Starting signOut');
    try {
      const cognitoUser = userPool.getCurrentUser();
      console.log('Current Cognito user:', cognitoUser ? 'exists' : 'null');
      
      // Clear all localStorage items
      localStorage.clear();
      sessionStorage.clear();
      
      if (cognitoUser) {
        cognitoUser.signOut();
        console.log('Cognito user signed out');
      }
      
      // Explicitly remove our persisted items
      localStorage.removeItem('authToken');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('idToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('systemInfo');
      
      setUserWithPersistence(null);
      setSystemInfo(null);
      setAuthToken(null);
      currentUser = null;
      
      console.log('AuthContext: Sign out completed');
      return true;
    } catch (error) {
      console.error('Error during sign out:', error);
      setUserWithPersistence(null);
      setSystemInfo(null);
      setAuthToken(null);
      currentUser = null;
      localStorage.clear();
      sessionStorage.clear();
      return true;
    }
  };

  const getAuthToken = async (): Promise<string | null> => {
    if (authToken) {
      return authToken;
    }
  
    const storedToken = localStorage.getItem('authToken');
    if (storedToken) {
      setAuthToken(storedToken);
      return storedToken;
    }
  
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      try {
        const session = await new Promise<any>((resolve, reject) => {
          cognitoUser.getSession((err: any, session: any) => {
            if (err) {
              reject(err);
            } else {
              resolve(session);
            }
          });
        });
  
        if (session.isValid()) {
          const token = session.getIdToken().getJwtToken();
          setAuthToken(token);
          localStorage.setItem('authToken', token);
          return token;
        }
      } catch (error) {
        console.error('Error getting session:', error);
      }
    }
  
    return null;
  };

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const contextValue = {
    user,
    isAuthenticated: !!user,
    isLoading,
    isAdmin,
    signInWithGoogle,
    handleOAuthCallback,
    signOut,
    getAuthToken,
    systemInfo,
    setSystemInfo,
    hasPermission,
    checkAuthStatus,
    refreshSession
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Export the hook
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Export the user pool if needed elsewhere
export { userPool };

// Export constants for use in components
export { PERMISSIONS, ROLE_PERMISSIONS };